import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import ErrorIcon from '@material-ui/icons/Error';
import useMobileLayout from 'Hooks/useMobileLayout';
import { navigateToClientPage } from 'Views/Page/Content/Templates';
import {
  FDBodyText, FDBox, FDHeadlineText, FDPaper, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { relationshipType } from 'facet-rest/src/consts/relationships';

const styles = (theme) => ({
  root: {
    maxWidth: theme.dimensions.xl,
    width: '80%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: theme.radius.sm,
    alignContent: 'center',
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '& > span > span > svg:first-child': {
      fontSize: '36px',
    },
  },
  rootMobile: {
    width: 'auto',
    '& > span > span > svg:first-child': {
      fontSize: '20px',
    },
  },
  importantIcon: {
    display: 'flex',
    color: theme.palette.warning.main,
    padding: theme.spacing(0.75),
    borderRadius: '50%',
    backgroundColor: theme.palette.facet.accentYellow1,
    width: '50px',
    height: '50px',
    justifyContent: 'center',
    alignItems: 'center',
    '& > svg': {
      width: '30px',
      height: '30px',
    },
    flex: '0 0 auto',
  },
  importantIconMobile: {
    width: '40px',
    height: '40px',
    '& > svg': {
      width: '24px',
      height: '24px',
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    maxWidth: '52ch',
  },
});

const HighPriorityActionCard = ({
  classes,
  title,
  subtitle,
  relationship,
  'data-test': test,
}) => {
  const isMobile = useMobileLayout();
  const { loadModalCollectionByLink } = useModalContext();

  async function handleClick() {
    switch (relationship.meta('type')) {
      case relationshipType.modalLink:
        await loadModalCollectionByLink(relationship.link('related').url());
        break;
      default:
        navigateToClientPage(await relationship.link('related'));
    }
  }

  return (
    <FDPaper padding={isMobile ? 2 : 4} maxWidth={720} minWidth={280} flex={1}>
      <FDStack direction="column" spacing={4} height="100%">{/* The above FDPaper isn't display:flex, and we want a flex:1 in here to take up more height, so we need height="100%" */}
        <FDStack direction="column" spacing={2} flex={1}>
          <FDStack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <FDHeadlineText value={title} color="textSecondary" />
            <div className={classNames(classes.importantIcon, { [classes.importantIconMobile]: isMobile })}>
              <ErrorIcon />
            </div>
          </FDStack>
          <FDBox flex={1}>
            <FDBodyText value={subtitle} />
          </FDBox>
        </FDStack>
        <FDPrimaryActionButton label={relationship.meta('prompt')} onClick={handleClick} alignSelf="flex-start" data-test={test} />
      </FDStack>
    </FDPaper>
  );
};

HighPriorityActionCard.defaultProps = {
  title: 'Import action needed.',
  subtitle: 'Click to learn more.',
  'data-test': undefined,
};

HighPriorityActionCard.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  relationship: PropTypes.object.isRequired,
  'data-test': PropTypes.string,
};

export default withStyles(styles)(HighPriorityActionCard);
